import {
  InvitedUser,
  InvitedUserFields,
  InvitedUserStatus,
  Palette,
  sanitizeString,
  TranslatedLanguage,
} from 'hubbl-shared';
import {memo, useMemo} from 'react';
import styled from 'styled-components';
import {Api} from '../../api';
import {AssetIconLoadMore, AssetIconTrash} from '../../assets';
import {useAppDispatch, useI18n} from '../../hooks';
import {GlobalActions} from '../../store/global/GlobalActions';
import {IconButton} from '../buttons/icon-button/IconButton';
import {Table} from '../table/Table';
import {TableBody} from '../table/TableBody';
import {TableCell} from '../table/TableCell';
import {TextHeading5} from '../text/TextHeading5';
import {TextHeading7} from '../text/TextHeading7';

const StatusContainer = styled.div`
  margin-top: 20px;
  border: 1px solid ${Palette.almostBlackTransparent5};
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.thead`
  background-color: ${Palette.primarySubtleGreen};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 500;
  color: ${Palette.almostBlack};
  border-bottom: 1px solid ${Palette.almostBlackTransparent5};
`;

const StatusHeader = styled.div`
  padding: 16px;
  background-color: ${Palette.primarySubtleGreen};
  border-bottom: 1px solid ${Palette.almostBlackTransparent5};
`;

const StatusBadge = styled.div<{status: InvitedUserStatus}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
        return Palette.primaryCherokee;
      case 'accepted':
        return Palette.primaryPadua;
      case 'rejected':
        return Palette.systemError;
      default:
        return Palette.almostBlackTransparent5;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'accepted':
        return Palette.almostBlack;
      case 'rejected':
        return Palette.white;
      default:
        return Palette.almostBlack;
    }
  }};
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

interface InviteUsersStatusProps {
  invitedUsers: InvitedUser[];
  companyId: string;
  onInviteSuccess?: () => void;
}

const hasRequiredInviteMetadata = (invitedUser: InvitedUser): boolean => {
  const hasFullName = Boolean(invitedUser.fullName?.trim());
  const hasJobTitle = Boolean(invitedUser.jobTitle?.trim());
  const hasDepartment = Boolean(invitedUser.department);

  return hasFullName && hasJobTitle && hasDepartment;
};

const mapInvitedUserToPayload = (
  invitedUser: InvitedUser,
): InvitedUserFields => {
  const fallbackLabel = 'General';
  return {
    email: invitedUser.email,
    companyId: invitedUser.companyId,
    companyName: invitedUser.companyName,
    fullName: invitedUser.fullName || invitedUser.email,
    jobTitle: invitedUser.jobTitle || '',
    department: invitedUser.department || {
      id: sanitizeString(fallbackLabel),
      label: {
        [TranslatedLanguage.English]: fallbackLabel,
        [TranslatedLanguage.Danish]: fallbackLabel,
      },
    },
  };
};

export const InviteUsersStatus = memo(
  ({invitedUsers, companyId, onInviteSuccess}: InviteUsersStatusProps) => {
    const dispatch = useAppDispatch();
    const t = useI18n();

    const invitedUsersCount = useMemo(() => {
      return invitedUsers.filter(user => user.status === 'pending').length;
    }, [invitedUsers]);

    const handleReinvite = async (invitedUser: InvitedUser) => {
      try {
        dispatch(GlobalActions.setIsBusy(true));
        if (hasRequiredInviteMetadata(invitedUser)) {
          await Api.invitedUsers.onInviteUsersByEmail({
            companyId,
            invitedUsers: [mapInvitedUserToPayload(invitedUser)],
          });
        } else {
          await Api.invitedUsers.reinvitePendingUser(invitedUser.email);
        }
        onInviteSuccess?.();
      } catch (err: any) {
        console.error('Failed to re-invite user:', err);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    };

    const handleDelete = async (email: string) => {
      try {
        dispatch(GlobalActions.setIsBusy(true));
        await Api.invitedUsers.deleteInvitedUser(companyId, email);
        onInviteSuccess?.();
      } catch (err: any) {
        console.error('Failed to delete invited user:', err);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    };

    if (invitedUsersCount === 0) return null;

    return (
      <StatusContainer>
        <StatusHeader>
          <TextHeading5
            text={`${t.common.pendingInvitations} (${invitedUsersCount})`}
          />
        </StatusHeader>
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell>
                <TextHeading7 text={t.common.email} />
              </TableHeaderCell>
              <TableHeaderCell>
                <TextHeading7 text={t.common.status} />
              </TableHeaderCell>
              <TableHeaderCell>
                <TextHeading7 text={t.common.invitedAt} />
              </TableHeaderCell>

              <TableHeaderCell style={{width: '160px'}}>
                <TextHeading7 text={t.common.actions} />
              </TableHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {invitedUsers.map(user => (
              <tr key={user.email}>
                <TableCell>
                  <TextHeading7 text={user.email} />
                </TableCell>
                <TableCell>
                  <StatusBadge status={user.status}>
                    {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <TextHeading7
                    text={new Date(user.invitedAt).toLocaleDateString()}
                  />
                </TableCell>

                <TableCell>
                  <ActionsContainer>
                    <IconButton
                      onClick={() => handleReinvite(user)}
                      icon={<AssetIconLoadMore />}
                      backgroundColor={Palette.primarySubtleGreen}
                      text={t.common.reInvite}
                    />
                    <IconButton
                      backgroundColor={Palette.primarySubtleGreen}
                      onClick={() => handleDelete(user.email)}
                      icon={<AssetIconTrash />}
                    />
                  </ActionsContainer>
                </TableCell>
              </tr>
            ))}
          </TableBody>
        </Table>
      </StatusContainer>
    );
  },
);
